import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle, XCircle, ArrowRight, Stack, ArrowClockwise } from "@phosphor-icons/react";
import { Checkbox } from "@/components/ui/checkbox";
import { RejectDialog } from "@/components/RejectDialog";
import { MemberDetailsDialog } from "@/components/MemberDetailsDialog";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { toast } from "sonner";

function Stepper({ status }) {
  const steps = [
    { key: "pending_regional", label: "Regional" },
    { key: "pending_national", label: "National" },
    { key: "approved", label: "ID Assigned" },
  ];
  const order = { incomplete: 0, pending_regional: 0, pending_national: 1, approved: 3, rejected: 0 };
  const done = order[status] ?? 0;
  return (
    <div className="flex items-center gap-1.5">
      {steps.map((s, i) => (
        <div key={s.key} className="flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${i < done ? "bg-emerald-500" : i === done ? "bg-primary" : "bg-border"}`} />
          <span className={`text-[10px] uppercase tracking-wide ${i < done ? "text-emerald-600 dark:text-emerald-400" : i === done ? "text-primary" : "text-muted-foreground"}`}>{s.label}</span>
          {i < steps.length - 1 && <ArrowRight size={10} className="text-muted-foreground mx-0.5" />}
        </div>
      ))}
    </div>
  );
}

export default function Approvals() {
  const { user } = useAuth();
  const national = user.role === "national_manager" || user.role === "super_admin";
  const [tab, setTab] = useState(national ? "pending_national" : "pending_regional");
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/members", { params: { status: tab, member_type: "public", page_size: 1000 } });
    setItems(data.items);
    setSelected([]);
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const ptr = usePullToRefresh(load);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allSelected = items.length > 0 && selected.length === items.length;
  const toggleAll = () => setSelected(allSelected ? [] : items.map((m) => m.id));
  const canApproveHere = tab === "pending_regional" || (tab === "pending_national" && national);

  const bulkApprove = async () => {
    setBulkBusy(true);
    try {
      const stage = tab === "pending_regional" ? "regional" : "national";
      const { data } = await api.post("/members/bulk-approve", { ids: selected, stage });
      toast.success(`Approved ${data.approved}${data.skipped ? `, skipped ${data.skipped}` : ""}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBulkBusy(false);
    }
  };

  const act = async (id, path, msg, body) => {
    setBusyId(id);
    try {
      await api.post(`/members/${id}/${path}`, body || {});
      toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Layout>
      <div className="animate-fade-up max-w-4xl" {...ptr.handlers}>
        {(ptr.pull > 0 || ptr.refreshing) && (
          <div className="md:hidden flex items-center justify-center text-muted-foreground overflow-hidden"
            style={{ height: ptr.refreshing ? 32 : ptr.pull }} data-testid="approvals-ptr">
            <ArrowClockwise size={20} weight="bold" className={ptr.refreshing ? "animate-spin" : ""}
              style={{ transform: ptr.refreshing ? "none" : `rotate(${ptr.pull * 3}deg)` }} />
          </div>
        )}
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Workflow</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Membership Approvals</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Applications flow from Regional approval to National approval. National approval assigns the next Membership ID.
        </p>

        <Tabs value={tab} onValueChange={setTab} className="mt-6">
          <TabsList>
            <TabsTrigger value="pending_regional" data-testid="tab-pending-regional">Awaiting Regional</TabsTrigger>
            {national && <TabsTrigger value="pending_national" data-testid="tab-pending-national">Awaiting National</TabsTrigger>}
            <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="mt-6 space-y-3">
          {items.length > 0 && canApproveHere && (
            <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/40 px-4 py-2.5" data-testid="bulk-action-bar">
              <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="bulk-select-all" />
              <span className="text-sm text-muted-foreground">
                {selected.length > 0 ? `${selected.length} selected` : "Select all"}
              </span>
              <Button size="sm" className="ml-auto gap-1.5" disabled={selected.length === 0 || bulkBusy}
                onClick={bulkApprove} data-testid="bulk-approve-button">
                <CheckCircle size={16} weight="bold" />
                {tab === "pending_national"
                  ? `Approve & Assign ID (${selected.length})`
                  : `Approve selected (${selected.length})`}
              </Button>
            </div>
          )}
          {items.length === 0 && (
            <Card className="border-dashed border-border p-10 text-center">
              <Stack size={28} className="mx-auto text-muted-foreground" />
              <p className="mt-3 text-sm text-muted-foreground">Nothing in this queue right now.</p>
            </Card>
          )}
          {items.map((m) => (
            <Card key={m.id} data-testid={`approval-card-${m.staff_id}`}
              className="p-5 border-border flex flex-col sm:flex-row sm:items-center gap-4 justify-between transition-transform duration-200 hover:-translate-y-0.5">
              <div className="flex items-center gap-4 min-w-0">
                {canApproveHere && (
                  <Checkbox checked={selected.includes(m.id)} onCheckedChange={() => toggle(m.id)}
                    data-testid={`select-${m.staff_id}`} />
                )}
                <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <button type="button" onClick={() => setDetailsTarget(m)}
                    data-testid={`approval-name-${m.staff_id}`}
                    className="font-semibold text-left hover:underline underline-offset-2 hover:text-primary transition-colors">
                    {m.first_name} {m.last_name}
                  </button>
                  {m.overdue && (
                    <span data-testid={`overdue-${m.staff_id}`} className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-destructive/15 text-destructive">
                      Overdue · {m.days_waiting}d
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground font-mono mb-2">{[m.staff_id, m.professional_grade].filter(Boolean).join(" · ") || "—"}</p>
                <Stepper status={m.application_status} />
                {tab === "rejected" && m.rejection_reason && (
                  <p className="text-xs text-destructive mt-2" data-testid={`reject-reason-${m.staff_id}`}>Reason: {m.rejection_reason}</p>
                )}
                </div>
              </div>
              <div className="flex gap-2 shrink-0 max-sm:w-full">
                {tab === "rejected" ? (
                  <Button size="sm" disabled={busyId === m.id}
                    onClick={() => act(m.id, "unreject", "Returned to the approval queue")}
                    data-testid={`unreject-${m.staff_id}`} className="gap-1.5 max-sm:flex-1">
                    <ArrowClockwise size={16} weight="bold" /> Unreject
                  </Button>
                ) : (
                  <>
                    {tab === "pending_regional" && (
                      <Button size="sm" disabled={busyId === m.id}
                        onClick={() => act(m.id, "approve-regional", "Approved at regional level")}
                        data-testid={`approve-regional-${m.staff_id}`} className="gap-1.5 max-sm:flex-1">
                        <CheckCircle size={16} weight="bold" /> Approve
                      </Button>
                    )}
                    {tab === "pending_national" && national && (
                      <Button size="sm" disabled={busyId === m.id}
                        onClick={() => act(m.id, "approve-national", "Approved — Membership ID assigned")}
                        data-testid={`approve-national-${m.staff_id}`} className="gap-1.5 max-sm:flex-1 bg-emerald-600 hover:bg-emerald-700 text-white">
                        <CheckCircle size={16} weight="bold" /> Approve & Assign ID
                      </Button>
                    )}
                    <Button size="sm" variant="outline" disabled={busyId === m.id}
                      onClick={() => setRejectTarget(m)}
                      data-testid={`reject-${m.staff_id}`}
                      className="gap-1.5 max-sm:flex-1 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground">
                      <XCircle size={16} weight="bold" /> Reject
                    </Button>
                  </>
                )}
              </div>
            </Card>
          ))}
        </div>
      </div>

      <RejectDialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}
        name={rejectTarget ? `${rejectTarget.first_name} ${rejectTarget.last_name}` : ""}
        onConfirm={(reason) => act(rejectTarget.id, "reject", "Application rejected", { reason })} />

      <MemberDetailsDialog member={detailsTarget} open={!!detailsTarget}
        onOpenChange={(o) => !o && setDetailsTarget(null)}
        footerSlot={detailsTarget && (
          tab === "rejected" ? (
            <Button size="sm" disabled={busyId === detailsTarget.id}
              data-testid="details-unreject"
              onClick={async () => { await act(detailsTarget.id, "unreject", "Returned to the approval queue"); setDetailsTarget(null); }}
              className="gap-1.5">
              <ArrowClockwise size={16} weight="bold" /> Unreject
            </Button>
          ) : (
          <>
            {tab === "pending_regional" && (
              <Button size="sm" disabled={busyId === detailsTarget.id}
                data-testid="details-approve-regional"
                onClick={async () => { await act(detailsTarget.id, "approve-regional", "Approved at regional level"); setDetailsTarget(null); }}
                className="gap-1.5">
                <CheckCircle size={16} weight="bold" /> Approve
              </Button>
            )}
            {tab === "pending_national" && national && (
              <Button size="sm" disabled={busyId === detailsTarget.id}
                data-testid="details-approve-national"
                onClick={async () => { await act(detailsTarget.id, "approve-national", "Approved — Membership ID assigned"); setDetailsTarget(null); }}
                className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white">
                <CheckCircle size={16} weight="bold" /> Approve & Assign ID
              </Button>
            )}
            <Button size="sm" variant="outline" disabled={busyId === detailsTarget.id}
              data-testid="details-reject"
              onClick={() => { setRejectTarget(detailsTarget); setDetailsTarget(null); }}
              className="gap-1.5 border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground">
              <XCircle size={16} weight="bold" /> Reject
            </Button>
          </>
          )
        )} />
    </Layout>
  );
}
